import { useEffect, useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { Group as TeamIcon } from '@mui/icons-material'
import { usersApi } from '../../services/api'
import { teamRoleRows, type ClientTeam } from '../../utils/clientTeam'

interface ClientTeamRowProps {
  team: ClientTeam
  /** Present only for users who may save a team (admin or manager). */
  onAssign?: () => void
}

export default function ClientTeamRow({ team, onAssign }: ClientTeamRowProps) {
  const [nameByEmail, setNameByEmail] = useState<Record<string, string>>({})

  useEffect(() => {
    let cancelled = false
    usersApi
      .list({ status: 'active' })
      .then((users) => {
        if (cancelled) return
        setNameByEmail(Object.fromEntries(users.filter((u) => u.name).map((u) => [u.email, u.name])))
      })
      .catch((err) => {
        // Names fall back to the email's local part, so the row still reads correctly.
        console.error('Failed to load team member names:', err)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const rows = teamRoleRows(team, nameByEmail)

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      spacing={2}
      useFlexGap
      sx={{ flexWrap: 'wrap', mb: 3 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
          Team
        </Typography>
        <Stack
          component="dl"
          direction="row"
          spacing={4}
          useFlexGap
          sx={{ flexWrap: 'wrap', m: 0 }}
        >
          {rows.map((row) => (
            <Box key={row.key} sx={{ minWidth: 0 }}>
              <Typography component="dt" variant="body2" color="text.secondary">
                {row.label}
              </Typography>
              <Typography
                component="dd"
                variant="body1"
                color={row.name ? 'text.primary' : 'text.secondary'}
                sx={{ m: 0, wordBreak: 'break-word' }}
              >
                {row.name ?? 'Unassigned'}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
      {onAssign && (
        <Button variant="outlined" startIcon={<TeamIcon />} onClick={onAssign} sx={{ minHeight: 44 }}>
          Assign team
        </Button>
      )}
    </Stack>
  )
}
